import React from 'react';
import Button from '../../v2BaseComponents/Button';
import { getIntl } from '../../../utils/intl';
import { useLoadedUser } from '../../../hooks/api/useUser';
import * as Linking from 'expo-linking';
import { privacyPolicyUrl, termsOfServiceUrl } from '../../../utils/constants';
import AccountHeader from './AccountHeader';
import { useAccountTabNavigation } from '../../../utils/appNavigationNames';
import { useIsWebDesktop } from '../../../hooks/useIsWebDesktop';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';
import { useSignOutAndResetApp } from '../../../hooks/useSignOutAndResetApp';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import usePayments from '../../../hooks/api/usePayments';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import Text from '../../v2BaseComponents/Text';
import { useRedirectToFeedbackAndRoadmap } from '../../../hooks/useRedirectToFeedbackAndRoadmap';
import AccountScreenRegistrationCodeInput from '../RegistrationCode/AccountScreenRegistrationCodeInput';

const intl = getIntl('myAccount', {
    accountInformation: 'Account Information',
    subscriptionPlans: 'Subscription Plans',
    support: 'Support',
    preferences: 'Preferences',
    about: 'About',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    feedbackAndRoadmap: 'Feedback & Roadmap',
    deleteAccount: 'Delete Account',
    signOut: 'Sign Out',
    accountNavigation: 'Account Sections',
    legalDisclaimer: 'Primary Record® is a registered trademark of Primary Record Inc. All rights reserved.',
});

export type ConsumerAppAccountLandingScreenOwnProps = {};

export type ConsumerAppAccountLandingScreenProps = ConsumerAppAccountLandingScreenOwnProps;

export const ConsumerAppAccountLandingScreen: React.FC<ConsumerAppAccountLandingScreenProps> = () => {
    const { redirectToFeedbackAndRoadmap, isRedirecting } = useRedirectToFeedbackAndRoadmap();
    const { user } = useLoadedUser({});
    const navigator = useAccountTabNavigation();
    const isWebDesktop = useIsWebDesktop();
    const { signOutAndResetApp, isResetting } = useSignOutAndResetApp();
    const { isLoadingPaymentStatus, isLoadingRegistrationCodeStatus } = usePayments({});

    return (
        <>
            <AccountHeader user={user} />
            <ActionButtonLayout
                whiteBackground
                scrollable
                actions={[
                    {
                        icon: 'LogOut',
                        title: intl('signOut'),
                        isDisabled: isResetting,
                        onPress: signOutAndResetApp,
                        testID: 'action-signOut',
                    },
                    {
                        icon: 'Lock',
                        title: intl('privacyPolicy'),
                        onPress: () => Linking.openURL(privacyPolicyUrl),
                        displayInMoreMenu: true,
                    },
                    {
                        icon: 'FileText',
                        title: intl('termsOfService'),
                        onPress: () => Linking.openURL(termsOfServiceUrl),
                        displayInMoreMenu: true,
                    },
                ]}
            >
                <AnimateLoadedContent
                    isLoading={isLoadingPaymentStatus || isLoadingRegistrationCodeStatus || isRedirecting}
                >
                    {() => (
                        <MainContentContainer flex={1}>
                            <Stack vertical fullWidth fullHeight justify="space-between">
                                <Stack vertical fullWidth space={contentStackSpacing} align="center">
                                    <AccountScreenRegistrationCodeInput />
                                    <Stack vertical fullWidth space={contentStackSpacing} align="center">
                                        <Button
                                            // @ts-expect-error TS2345
                                            onPress={() => navigator.navigate('information')}
                                            title={intl('accountInformation')}
                                            variant="list-button"
                                            testID="account-information-button"
                                            size="lg"
                                            icon="ChevronRight"
                                        />
                                        <Button
                                            onPress={() => navigator.navigate('preferences')}
                                            title={intl('preferences')}
                                            variant="list-button"
                                            testID="preferences-button"
                                            size="lg"
                                            icon="ChevronRight"
                                        />
                                        <Button
                                            onPress={() => navigator.navigate('subscription')}
                                            title={intl('subscriptionPlans')}
                                            variant="list-button"
                                            testID="subscription-plans-button"
                                            size="lg"
                                            icon="ChevronRight"
                                        />
                                        {!isWebDesktop ? (
                                            <Button
                                                onPress={() => navigator.navigate('support')}
                                                title={intl('support')}
                                                variant="list-button"
                                                testID="support-button"
                                                size="lg"
                                                icon="ChevronRight"
                                            />
                                        ) : null}
                                        <Button
                                            onPress={redirectToFeedbackAndRoadmap}
                                            title={intl('feedbackAndRoadmap')}
                                            variant="list-button"
                                            testID="feedback-and-roadmap-button"
                                            size="lg"
                                            icon="ChevronRight"
                                        />
                                    </Stack>
                                </Stack>
                                <Stack horizontal fullWidth justify="center">
                                    <Text variant="caption-1" alignText="center">
                                        {intl('legalDisclaimer', {
                                            year: new Date().getFullYear(),
                                        })}
                                    </Text>
                                </Stack>
                            </Stack>
                        </MainContentContainer>
                    )}
                </AnimateLoadedContent>
            </ActionButtonLayout>
        </>
    );
};

export default React.memo(ConsumerAppAccountLandingScreen);
